import { useCallback, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { 
	getMST4030_R01HeaderList, 
	getMST4030_R01HeaderAllCount,
	getMST4030_R01DetailList,
	getMST4030_R01DetailAllCount
} from "./Api_MST4030_R01";

import { showError, ShowSuccessFetch, showWarning, showSuccess } from "src/utils/toast/toastUtil";
import { defaultMST4030_R01HeaderDto, MST4030_R01HeaderDto, MST4030_R01DetailDto } from "./Dto_MST4030_R01";
import { defaultMST4030_R01Filter, MST4030_R01Filter, useMST4030_R01Store } from "./Store_MST4030_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { defaultMST4030_R01DetailDto } from "./Dto_MST4030_R01";
import { 
	createMST4030_R01Header, 
	patchMST4030_R01Header, 
	deleteMST4030_R01Header,
	createMST4030_R01Detail,
	patchMST4030_R01Detail,
	deleteMST4030_R01Detail,
	downloadExcel_MST4030_R01
} from "./Api_MST4030_R01";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { formatDate } from "src/utils/string/stringUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import { buildCommonCodeRequirements, MST4030_R01_DETAIL_ROW_VALIDATE, MST4030_R01_HEADER_ROW_VALIDATE } from "src/utils/commonCode/commonCodeRequirementConfig";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";

interface Props {
	gridRef: React.RefObject<AgGridReact<MST4030_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<any> | null>;
	setMessage: (message: string) => void;
	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
}

export const Content_MST4030_R01 = (props: Props) => {

	const {
		gridRef,
		detailGridRef,
		setMessage,
		containerRef,
		isResizing,
	} = props;

	const {
		MST4030_R01Filter, setMST4030_R01Filter,
		MST4030_R01PastFilter, setMST4030_R01PastFilter,
		MST4030_R01List, setMST4030_R01List,
		selectedMST4030_R01, setSelectedMST4030_R01,
		editMST4030_R01, setEditMST4030_R01,
		MST4030_R01DetailList, setMST4030_R01DetailList,
		insertMST4030_R01DetailList,
		deletedMST4030_R01DetailList,
		setDeletedMST4030_R01DetailList,
		selectedMST4030_R01DetailList,
		setSelectedMST4030_R01DetailList,
		addDeletedMST4030_R01DetailList,
		removeSelectedMST4030_R01DetailList,
		originalMST4030_R01DetailList,
		setOriginalMST4030_R01DetailList,
		allCount, setAllCount,
		detailAllCount, setDetailAllCount,
		detailFetchTrigger, setDetailFetchTrigger,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = useMST4030_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	const { eqmStatusList, inspectionStatusList, compCdList, orgCdList, workPlaceList } = useCommonCodeStore();

	useFetchActionbarAuth("MST4030_R01");

	const { printBulkEquipmentLabel, printBulkToolingLabel } = useQzPrint();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	/**
	 * 엑셀 다운로드 핸들러 (특정 설비의 헤더 + 디테일)
	 */
	const handleExcelClick = useCallback(async () => {
		// 선택된 설비 검증
		if (!selectedMST4030_R01?.equipmentCd) {
			showWarning('엑셀 다운로드할 설비를 선택해주세요.');
			return;
		}

		setLoadingSpinner(true);

		try {
			const response = await downloadExcel_MST4030_R01(selectedMST4030_R01.equipmentCd);

			const fileName = `설비_${selectedMST4030_R01.equipmentCd}_점검이력.xlsx`;
			downloadSettingExcel({ responseData: response.data.data, fileName });

			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error: any) {
			console.error("엑셀 다운로드 중 오류 발생:", error);
			
			// 400 또는 404 에러 처리
			if (error.response?.status === 400 || error.response?.status === 404) {
				const errorData = error.response.data;
				showError(errorData?.message || '설비를 찾을 수 없습니다.');
			} else {
				showError(error.response?.data?.message || "엑셀 다운로드 중 오류가 발생했습니다.");
			}
		}

		setLoadingSpinner(false);
	}, [selectedMST4030_R01, setLoadingSpinner]);

	/** 액션바 출력: 테이블에서 선택한 설비 1건 라벨 (대량 출력과 동일 QZ/브라우저 경로) */
	const handlePrintClick = useCallback(async () => {
		if (!selectedMST4030_R01?.equipmentCd) {
			showWarning('출력할 설비를 테이블에서 선택해주세요.');
			return;
		}

		setLoadingSpinner(true);
		try {
			const labelData = [{
				equipmentCd: selectedMST4030_R01.equipmentCd,
				equipmentNm: selectedMST4030_R01.equipmentNm ?? '',
				equipmentSpec: selectedMST4030_R01.equipmentSpec ?? '',
			}];

			if (selectedMST4030_R01.equipmentType === 'METALMASK') {
				await printBulkToolingLabel(labelData);
			} else {
				await printBulkEquipmentLabel(labelData);
			}
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedMST4030_R01, printBulkEquipmentLabel, printBulkToolingLabel, setLoadingSpinner]);

	/**
	 * 필터 파라미터 생성
	 */
	const getParams = (filter: MST4030_R01Filter) => {
		const params: Partial<MST4030_R01Filter> = {};
		if (filter.compCd) params.compCd = filter.compCd;
		if (filter.workplaceCd) params.workplaceCd = filter.workplaceCd;
		if (filter.equipmentType) params.equipmentType = filter.equipmentType;
		if (filter.equipmentCd) params.equipmentCd = filter.equipmentCd;
		if (filter.eqmStatus) params.eqmStatus = filter.eqmStatus;
		return params;
	}

	/**
	 * 헤더 전체 개수 갱신
	 */
	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST4030_R01HeaderAllCount(MST4030_R01Filter);
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [MST4030_R01Filter, setAllCount]);

	/**
	 * 헤더 목록 조회 (TOOLING 설비 목록)
	 */
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);

		try {
			const response = await getMST4030_R01HeaderList(
				'search',
				getParams(MST4030_R01Filter),
				MST4030_R01List.length ?? 0,
				0
			);

			let newList = [...response.data.data] as MST4030_R01HeaderDto[];
			setMST4030_R01List(newList);

			const processAllCount = await renewalAllCount();

			if (newList && newList.length > 0) {
				// 데이터가 있으면 첫 번째 행 선택
				gridRefSelectedRowHandler(
					gridRef,
					setSelectedMST4030_R01,
					() => true
				);
				setMST4030_R01PastFilter(MST4030_R01Filter);
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			} else {
				showWarning('조회된 데이터가 없습니다.');
				handleCreateClick();
			}
		} catch (error: any) {
			console.error("조회 중 오류 발생:", error);
			showError(error.response?.data?.message || "조회 중 오류가 발생했습니다.");
			setSelectedMST4030_R01(undefined);
			setMST4030_R01DetailList([]);
		}

		setLoadingSpinner(false);
		setTimeout(() => setMessage(""), 5000);
	}, [
		MST4030_R01Filter, 
		MST4030_R01List, 
		gridRef, 
		setMST4030_R01List, 
		setSelectedMST4030_R01,
		setMST4030_R01DetailList,
		setMST4030_R01PastFilter,
		renewalAllCount,
		setMessage,
		setLoadingSpinner,
		setScrollTop,
		setDetailScrollTop,
	]);

	/**
	 * 디테일 목록 조회 (점검 이력)
	 */
	const fetchDetailData = useCallback(async (eqmCd: string) => {
		if (!detailFetchTrigger) return;
		if (!eqmCd) {
			setMST4030_R01DetailList([]);
			return;
		}

		setLoadingSpinner(true);

		try {
			const response = await getMST4030_R01DetailList(
				'search',
				{ ...MST4030_R01Filter, eqmCd },
				0,
				0
			);

			const detailList = [...response.data.data];
			setMST4030_R01DetailList(detailList);
			// 원본 데이터 저장 (수정 시 복합키 필드 참조용)
			setOriginalMST4030_R01DetailList(detailList);

			// 디테일 전체 개수 조회
			const detailCountResponse = await getMST4030_R01DetailAllCount({
				...MST4030_R01Filter,
				eqmCd
			});
			setDetailAllCount(detailCountResponse.data.data);

			if (detailList.length > 0) {
				setMessage(`${selectedMST4030_R01?.equipmentNm || eqmCd}의 점검 이력 ${detailList.length}건을 조회하였습니다.`);
				setTimeout(() => setMessage(""), 5000);
			} else {
				// 데이터가 없는 경우 기본 행 추가
				setMST4030_R01DetailList([]);
				insertMST4030_R01DetailList([{ ...defaultMST4030_R01DetailDto, inserted: true }]);
				setMessage(`${selectedMST4030_R01?.equipmentNm || eqmCd}의 점검 이력이 없습니다. 새로 추가할 수 있습니다.`);
				setTimeout(() => setMessage(""), 3000);
			}
		} catch (error: any) {
			console.error("점검 이력 조회 중 오류 발생:", error);
			showError(error.response?.data?.message || "점검 이력 조회 중 오류가 발생했습니다.");
			setMST4030_R01DetailList([]);
			insertMST4030_R01DetailList([{ ...defaultMST4030_R01DetailDto, inserted: true }]);
		}

		setLoadingSpinner(false);
		setDetailFetchTrigger(false);
	}, [
		MST4030_R01Filter,
		selectedMST4030_R01,
		setMST4030_R01DetailList,
		setOriginalMST4030_R01DetailList,
		setDetailAllCount,
		setMessage,
		setLoadingSpinner,
		detailGridRef
	]);

	const defaultMST4030_R01HeaderRow = useCallback(() => {
		
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST4030_R01_HEADER_ROW_VALIDATE, {
				eqmStatusList,
				compCdList,
				orgCdList,
				workPlaceList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultMST4030_R01HeaderDto }; }

		return {
			...defaultMST4030_R01HeaderDto,
			equipmentType: 'METALMASK',
			eqmStatus: eqmStatusList[0]?.minorCd,
			compCd: compCdList[0]?.compCd,
			eqmOrgcd: orgCdList[0]?.orgCd,
			workplaceCd: workPlaceList[0]?.workplaceCd,
		};
	}, [defaultMST4030_R01HeaderDto, eqmStatusList, compCdList, orgCdList, workPlaceList, validateCommonCodeLists]);

	const defaultMST4030_R01DetailRow = useCallback(() => {

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST4030_R01_DETAIL_ROW_VALIDATE, {
				inspectionStatusList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultMST4030_R01DetailDto }; }

			return {
				...defaultMST4030_R01DetailDto,
				inspectType: inspectionStatusList[0]?.minorCd ?? defaultMST4030_R01DetailDto.inspectType,
				inserted: true,
			}
		},
		[defaultMST4030_R01DetailDto, inspectionStatusList, validateCommonCodeLists]
	);


	/**
	 * 초기화 핸들러
	 */
	const initContainerData = useCallback(() => {
		initData();
		// 초기화 후에도 기본 설비상태는 'GOOD'으로 유지
		setMST4030_R01Filter({ ...defaultMST4030_R01Filter, eqmStatus: 'GOOD' });
	}, [initData, setMST4030_R01Filter]);

	/**
	 * 생성 버튼 클릭 핸들러
	 * Info 폼 비우기, Detail 테이블 비우기, Detail 테이블에 신규 행 추가
	 */
	const handleCreateClick = useCallback(() => {
		// 삭제 목록 초기화
		setDeletedMST4030_R01DetailList([]);
		
		// 선택 목록 초기화
		setSelectedMST4030_R01DetailList([]);
		
		// 원본 목록 초기화
		setOriginalMST4030_R01DetailList([]);
		
		// Detail 테이블 비우고 신규 행 하나 추가 (점검유형 등 공통코드 기본값 반영)
		setMST4030_R01DetailList([{ ...defaultMST4030_R01DetailRow() }]);

		// Info: 설비유형·사업장·관리부서·공정 등 공통코드 첫 항목으로 채움 (저장 시 빈값 방지)
		setEditMST4030_R01({ ...defaultMST4030_R01HeaderRow() });

		// 헤더 선택 해제 (마지막에 처리하여 useEffect와의 충돌 방지)
		setSelectedMST4030_R01(undefined);
	}, [
		defaultMST4030_R01HeaderRow,
		defaultMST4030_R01DetailRow,
		setSelectedMST4030_R01,
		setEditMST4030_R01,
		setMST4030_R01DetailList,
		setDeletedMST4030_R01DetailList,
		setSelectedMST4030_R01DetailList,
		setOriginalMST4030_R01DetailList
	]);

	/**
	 * 삭제 버튼 클릭 핸들러
	 * - 디테일 행이 선택된 경우: 디테일 삭제 표시
	 * - 디테일 행이 선택되지 않은 경우: 헤더(설비) 삭제
	 */
	const handleDeleteClick = useCallback(async () => {
		// 디테일 행이 선택된 경우: 디테일 삭제 표시
		if (selectedMST4030_R01DetailList.length > 0) {
			// 선택된 디테일 행을 삭제 목록에 추가
			addDeletedMST4030_R01DetailList(selectedMST4030_R01DetailList);
			removeSelectedMST4030_R01DetailList(selectedMST4030_R01DetailList);
			showSuccess(`선택된 점검 이력 ${selectedMST4030_R01DetailList.length}건이 삭제 표시되었습니다. 저장 시 삭제됩니다.`);
			setTimeout(() => setMessage(""), 3000);
			return;
		}

		// 디테일 행이 선택되지 않은 경우: 헤더(설비) 삭제
		// 주석 처리: 헤더 삭제 기능 비활성화
		/*
		if (!selectedMST4030_R01?.equipmentCd) {
			showWarning('삭제할 설비를 선택해주세요.');
			return;
		}

		if (!window.confirm(`${selectedMST4030_R01.equipmentCd} 설비를 삭제하시겠습니까?`)) {
			return;
		}

		setLoadingSpinner(true);

		try {
			const response = await deleteMST4030_R01Header(selectedMST4030_R01.equipmentCd);
			
			if (response.data.successFlag) {
				showSuccess(`${selectedMST4030_R01.equipmentCd} 설비가 삭제되었습니다.`);
				setMessage(`${selectedMST4030_R01.equipmentCd} 설비가 삭제되었습니다.`);
				
				// 목록에서 제거
				refetchUpdateList(
					'delete',
					selectedMST4030_R01,
					selectedMST4030_R01,
					'equipmentCd',
					MST4030_R01List,
					setMST4030_R01List
				);
				
				// 선택 해제 및 초기화
				setSelectedMST4030_R01(undefined);
				setEditMST4030_R01(defaultMST4030_R01HeaderDto);
				setMST4030_R01DetailList([]);
				
				// 전체 개수 갱신
				await renewalAllCount();
			} else {
				showError(response.data.message || '삭제 중 오류가 발생했습니다.');
			}
		} catch (error: any) {
			console.error("삭제 중 오류 발생:", error);
			showError(error.response?.data?.message || "삭제 중 오류가 발생했습니다.");
		}

		setLoadingSpinner(false);
		setTimeout(() => setMessage(""), 5000);
		*/
		
		// 헤더 삭제 비활성화 안내
		showWarning('헤더 삭제 기능은 비활성화되어 있습니다. 디테일 삭제만 가능합니다.');
	}, [
		selectedMST4030_R01,
		selectedMST4030_R01DetailList,
		MST4030_R01List,
		setMST4030_R01List,
		setSelectedMST4030_R01,
		setEditMST4030_R01,
		setMST4030_R01DetailList,
		addDeletedMST4030_R01DetailList,
		removeSelectedMST4030_R01DetailList,
		renewalAllCount,
		setMessage,
		setLoadingSpinner
	]);

	/**
	 * 저장 버튼 클릭 핸들러
	 * 헤더와 디테일을 모두 저장/수정/삭제 처리
	 */
	const handleSaveClick = useCallback(async () => {
		if (!editMST4030_R01?.equipmentCd) {
			showWarning('설비코드를 입력해주세요.');
			return;
		}

		setLoadingSpinner(true);

		try {
			let headerResponse;
			let headerProcessName = '';

			// 1. 헤더 저장/수정
			if (selectedMST4030_R01?.equipmentCd) {
				headerProcessName = 'patch';
				headerResponse = await patchMST4030_R01Header(editMST4030_R01);
			} else {
				headerProcessName = 'post';
				const row = defaultMST4030_R01HeaderRow();
				const orCode = (v: string | undefined, fb: string) =>
					v != null && String(v).trim() !== '' ? v : fb;
				headerResponse = await createMST4030_R01Header({
					...editMST4030_R01,
					equipmentType: orCode(editMST4030_R01.equipmentType, row.equipmentType),
					eqmStatus: orCode(editMST4030_R01.eqmStatus, row.eqmStatus),
					compCd: orCode(editMST4030_R01.compCd, row.compCd),
					eqmOrgcd: orCode(editMST4030_R01.eqmOrgcd, row.eqmOrgcd),
					workplaceCd: orCode(editMST4030_R01.workplaceCd, row.workplaceCd),
				});
			}

			if (!headerResponse.data.successFlag) {
				showError(headerResponse.data.message || '헤더 저장 중 오류가 발생했습니다.');
				setLoadingSpinner(false);
				return;
			}

			const savedHeader = headerResponse.data.data;

			// 2. 디테일 처리
			const deletedValueIndexList = deletedMST4030_R01DetailList.map((v) => v.toString());

			const insertedDetailList = MST4030_R01DetailList.filter(
				(row) =>
					!deletedValueIndexList.includes(row.valueIndex?.toString() || '') &&
					row.inserted === true,
			);

			const modifiedDetailList = MST4030_R01DetailList.filter(
				(row) =>
					!deletedValueIndexList.includes(row.valueIndex?.toString() || '') &&
					row.isModified === true &&
					row.inserted !== true,
			);

			const deletedDetailList = MST4030_R01DetailList.filter(
				(row) =>
					deletedValueIndexList.includes(row.valueIndex?.toString() || '') && !row.inserted,
			);

			const detailPromises: Promise<any>[] = [];

			insertedDetailList.forEach((detail) => {
				detailPromises.push(
					createMST4030_R01Detail({
						...detail,
						eqmCd: savedHeader.equipmentCd,
					}),
				);
			});

			modifiedDetailList.forEach((detail) => {
				const originalDetail = originalMST4030_R01DetailList.find(
					(orig) => orig.valueIndex === detail.valueIndex,
				);

				const detailData = {
					...detail,
					eqmCd: savedHeader.equipmentCd || editMST4030_R01.equipmentCd,
					inspectDt: detail.inspectDt,
					oldInspectDt: originalDetail?.inspectDt,
					oldEqmCd: originalDetail?.eqmCd,
					oldInspectCycle: originalDetail?.inspectCycle,
					oldInspectType: originalDetail?.inspectType,
				};

				detailPromises.push(patchMST4030_R01Detail(detailData));
			});

			deletedDetailList.forEach((detail) => {
				if (detail.inspectDt && detail.eqmCd && detail.inspectType) {
					detailPromises.push(
						deleteMST4030_R01Detail({
							inspectDt: detail.inspectDt,
							eqmCd: detail.eqmCd,
							inspectCycle: detail.inspectCycle || '',
							inspectType: detail.inspectType,
						}),
					);
				}
			});

			if (detailPromises.length > 0) {
				const results = await Promise.allSettled(detailPromises);
				const failedResults = results.filter((r) => r.status === 'rejected');
				if (failedResults.length > 0) {
					console.error('일부 디테일 작업 실패:', failedResults);
				}
			}

			let successMessage =
				headerProcessName === 'post'
					? `${savedHeader.equipmentCd} 설비 등록이 완료되었습니다.`
					: `${savedHeader.equipmentCd} 설비 수정이 완료되었습니다.`;

			if (insertedDetailList.length > 0) {
				successMessage += ` 점검 이력 ${insertedDetailList.length}건이 등록되었습니다.`;
			}
			if (modifiedDetailList.length > 0) {
				successMessage += ` 점검 이력 ${modifiedDetailList.length}건이 수정되었습니다.`;
			}
			if (deletedDetailList.length > 0) {
				successMessage += ` 점검 이력 ${deletedDetailList.length}건이 삭제되었습니다.`;
			}

			showSuccess(successMessage);
			setMessage(successMessage);

			try {
				const refetchResponse = await getMST4030_R01HeaderList(
					'search',
					{ ...MST4030_R01Filter, equipmentCd: savedHeader.equipmentCd },
					0,
					0,
				);

				if (refetchResponse.data.data?.length > 0) {
					const refetchedHeader = refetchResponse.data.data[0];
					refetchUpdateList(
						headerProcessName,
						refetchedHeader,
						editMST4030_R01,
						'equipmentCd',
						MST4030_R01List,
						setMST4030_R01List,
					);
					setSelectedMST4030_R01(refetchedHeader);
					setEditMST4030_R01(refetchedHeader);
				} else {
					refetchUpdateList(
						headerProcessName,
						savedHeader,
						editMST4030_R01,
						'equipmentCd',
						MST4030_R01List,
						setMST4030_R01List,
					);
					setSelectedMST4030_R01(savedHeader);
				}
			} catch (refetchError) {
				console.warn('헤더 재조회 실패:', refetchError);
				refetchUpdateList(
					headerProcessName,
					savedHeader,
					editMST4030_R01,
					'equipmentCd',
					MST4030_R01List,
					setMST4030_R01List,
				);
				setSelectedMST4030_R01(savedHeader);
			}

			if (
				insertedDetailList.length > 0 ||
				modifiedDetailList.length > 0 ||
				deletedDetailList.length > 0
			) {
				await fetchDetailData(savedHeader.equipmentCd);
			}

			await renewalAllCount();
		} catch (error: any) {
			console.error('저장 중 오류 발생:', error);
			showError(error.response?.data?.message || '저장 중 오류가 발생했습니다.');
		}

		setLoadingSpinner(false);
		setTimeout(() => setMessage(''), 5000);
	}, [
		editMST4030_R01,
		selectedMST4030_R01,
		defaultMST4030_R01HeaderRow,
		MST4030_R01DetailList,
		deletedMST4030_R01DetailList,
		originalMST4030_R01DetailList,
		MST4030_R01List,
		MST4030_R01Filter,
		setMST4030_R01List,
		setSelectedMST4030_R01,
		setEditMST4030_R01,
		fetchDetailData,
		renewalAllCount,
		setMessage,
		setLoadingSpinner,
	]);

	/**
	 * 최초 진입 시 기본 설비상태(eqmStatus)를 'GOOD'으로 설정
	 * - eqmStatus가 비어 있을 때만 설정하여, 사용자가 변경한 값은 유지
	 */
	useEffect(() => {
		if (!MST4030_R01Filter.eqmStatus) {
			setMST4030_R01Filter({ eqmStatus: 'GOOD' });
		}
	}, [MST4030_R01Filter.eqmStatus, setMST4030_R01Filter]);

	/**
	 * 헤더 선택 시 디테일 목록 자동 조회
	 */
	useEffect(() => {
		if (selectedMST4030_R01?.equipmentCd) {
			setEditMST4030_R01(selectedMST4030_R01);
			fetchDetailData(selectedMST4030_R01.equipmentCd);
		} else if (selectedMST4030_R01 === undefined) {
			const prev = useMST4030_R01Store.getState().editMST4030_R01;
			const row = defaultMST4030_R01HeaderRow();
			const pick = (cur: string | undefined, fb: string) =>
				cur != null && String(cur).trim() !== '' ? cur : fb;
			setEditMST4030_R01({
				...row,
				...prev,
				equipmentType: pick(prev.equipmentType, row.equipmentType),
				eqmStatus: pick(prev.eqmStatus, row.eqmStatus),
				compCd: pick(prev.compCd, row.compCd),
				eqmOrgcd: pick(prev.eqmOrgcd, row.eqmOrgcd),
				workplaceCd: pick(prev.workplaceCd, row.workplaceCd),
			});
		}
	}, [selectedMST4030_R01, fetchDetailData, setEditMST4030_R01, defaultMST4030_R01HeaderRow]);

	return {
		handleExcelClick,
		handlePrintClick,
		fetchData,
		defaultMST4030_R01HeaderRow,
		defaultMST4030_R01DetailRow,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	};
}

